import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { CashReceiptEnums } from 'src/app/enum/cash-receipt-enums';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { AdvancePaymentCaptureTable } from 'src/app/models/tables/wallet/advance-payment-capture-table.model';
import { AdvancePaymentTableModal } from 'src/app/models/tables/wallet/advance-payment-table-modal.model';
import { CashReceiptPaymentCaptureTableModel } from 'src/app/models/tables/wallet/cash-receipt-payment-capture-table.model';
import { AdvancePaymentAppliedModel } from 'src/app/models/wallet/advance-payment-applied.model';
import { CashReceiptModel } from 'src/app/models/wallet/cash-receipt.model';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { AdvancePaymentAppliedService } from 'src/app/services/wallet/advance-payment-applied.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-advance-payments-applied',
  templateUrl: './advance-payments-applied.component.html',
  styleUrls: ['./advance-payments-applied.component.scss'],
  standalone: false
})
export class AdvancePaymentsAppliedComponent implements OnInit {

  @ViewChild('advancePaymentsSelection') advancePaymentsSelectionModalComponent?: CustomModalComponent | null = null;

  @Input() cashReceiptModel: CashReceiptModel = new CashReceiptModel();
  @Input() disabledDetail: boolean = false;
  @Input() permissionUpdatable: boolean = true;
  @Output() quantityDetails = new EventEmitter<number>();
  @Output() updateAdvances = new EventEmitter<boolean>();

  advancePaymentAppliedContract: AdvancePaymentAppliedModel = new AdvancePaymentAppliedModel();
  ascendant: boolean = true;
  ascendantAdvancePayments: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  columnSortAdvancePayments: string | null = null;
  dataAdvancePayments: PagedResponseModel<AdvancePaymentTableModal> = new PagedResponseModel<AdvancePaymentTableModal>();
  detailData: PagedResponseModel<AdvancePaymentCaptureTable> = new PagedResponseModel<AdvancePaymentCaptureTable>();
  detailHeader: ColumnResponseModel[] = [];
  formAdvancePayment: FormGroup = new FormGroup({});
  headerAdvancePayments: ColumnResponseModel[] = [];
  inputsForAdvancePaymentsSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: false,
    showRecover: false,
    isMultipleSelect: false,
    filters: [],
  });
  isFormView: boolean = false;
  limit: number = 10;
  limitAdvancePayments: number = 10;
  output$ = new Subject<any>();
  page: number = 1;
  pageAdvancePayments: number = 1;
  subscription: Subscription;
  tableActions: TableAction[] = [];
  componentTableSelection = CustomTableSelectComponent;

  constructor(private advancePaymentAppliedService: AdvancePaymentAppliedService,
    private formBuilder: FormBuilder,
    private formControlStateService: FormControlStateService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(new AdvancePaymentAppliedModel());
  }

  async ngOnInit() {
    await this.getColumns();
    await this.getColumnsAdvancePayments();

    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeAdvancePayments(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cashReceiptModel'] && !changes['cashReceiptModel'].firstChange) {
      if (this.cashReceiptModel.cashReceiptId !== 0 && this.cashReceiptModel.cashReceiptId != null) {
        this.getAdvancePaymentsApplied();
      } else {
        this.detailData = new PagedResponseModel<AdvancePaymentCaptureTable>();
      }
    }
    if (changes['cashReceiptModel']) {
      if (changes['cashReceiptModel'].currentValue.cashReceiptStateId === CashReceiptEnums.abierto && this.permissionUpdatable) {
        this.tableActions = [
          {
            label: 'Editar',
            icon: 'pi pi-pencil',
            action: 'edit',
          }
        ];
      } else {
        this.tableActions = [];
      }
    }
  }

  cancel() {
    this.validateForm(new AdvancePaymentAppliedModel());
    this.getFormView(false);
  }

  changeAppliedValue() {
    let residueInMemory = this.advancePaymentAppliedContract.cashReceiptAdvance.totalValue - this.advancePaymentAppliedContract.appliedValue;
    const appliedValue = this.formAdvancePayment.controls['appliedValue'].value;
    if (appliedValue < 0) {
      this.formAdvancePayment.controls['residueValue'].patchValue(residueInMemory);
      this.formAdvancePayment.controls['appliedValue'].patchValue(this.advancePaymentAppliedContract.appliedValue);
      this.toastHandlerService.showToast('error', 'El valor aplicado no puede ser menor a 0.');
      return;
    }

    if (appliedValue > this.advancePaymentAppliedContract.cashReceiptAdvance.totalValue) {
      this.formAdvancePayment.controls['residueValue'].patchValue(residueInMemory);
      this.formAdvancePayment.controls['appliedValue'].patchValue(this.advancePaymentAppliedContract.appliedValue);
      this.toastHandlerService.showToast('error', 'El valor aplicado no puede ser mayor al saldo del anticipo.');
      return;
    }

    residueInMemory = this.advancePaymentAppliedContract.cashReceiptAdvance.totalValue - appliedValue;
    this.formAdvancePayment.controls['residueValue'].patchValue(residueInMemory);
  }

  async deleteAdvancePayments(cashReceiptAdvancesIds: number[]) {
    Swal.fire({
      title: 'Atención',
      text: '¿Realmente desea eliminar los anticipos aplicados seleccionados?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si',
      cancelButtonText: 'No'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await firstValueFrom(this.advancePaymentAppliedService.deleteAdvancePaymentApplied(cashReceiptAdvancesIds));
          this.toastHandlerService.showToast('success', 'Los anticipos aplicados seleccionados fueron eliminados exitosamente.');
          this.clearSelectionTrigger++;
          this.getAdvancePaymentsApplied();
          this.cancel();
          this.updateAdvances.emit(true);
        } catch (error) {
          console.error('Error al eliminar los anticipos aplicados seleccionados.', error);
        }
      }
    });
  }

  async getAdvancePaymentsApplied() {
    try {
      this.detailData = await firstValueFrom(this.advancePaymentAppliedService.getAppliedAdvancesByCashReceiptId(
        this.cashReceiptModel.cashReceiptId, this.page, this.limit, this.columnSort, this.ascendant
      ));
      this.quantityDetails.emit(this.detailData.items.length);
    } catch (error) {
      console.error('Error al buscar los anticipos aplicados al recibo de caja.', error);
    }
  }

  async getColumns() {
    try {
      this.detailHeader = await firstValueFrom(this.advancePaymentAppliedService.getColumnsForCaptureTable());
    } catch (error) {
      console.error('Error al buscar las columnas para los anticipos aplicados.', error);
    }
  }

  async getColumnsAdvancePayments() {
    try {
      if (this.headerAdvancePayments.length != 0) return;
      this.headerAdvancePayments = await firstValueFrom(this.advancePaymentAppliedService.getColumnsForAdvancePaymentsModal());
      this.updateInputs();
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de anticipos.', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  async getListAdvancePaymentsForSelection() {
    try {
      this.dataAdvancePayments = await firstValueFrom(this.advancePaymentAppliedService.getListAdvancePaymentsForSelection(
        this.cashReceiptModel.cashReceiptId, this.pageAdvancePayments, this.limitAdvancePayments,
        this.columnSortAdvancePayments, this.ascendantAdvancePayments
      ));
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los anticipos.', error);
    }
  }

  async handleSelectedItem(selectedItem: any) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para la edición de recibos de caja. Comuníquese con el administrador del sistema.'
      );
      return;
    }
    Swal.fire({
      title: 'Atención',
      text: '¿Realmente desea agregar los anticipos seleccionados?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si',
      cancelButtonText: 'No'
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (selectedItem.length == 1) {
          this.advancePaymentAppliedContract = new AdvancePaymentAppliedModel();
          this.advancePaymentAppliedContract.cashReceiptAppliedId = this.cashReceiptModel.cashReceiptId;
          this.advancePaymentAppliedContract.cashReceiptAdvanceId = selectedItem[0].cashReceiptId;
          this.advancePaymentAppliedContract.appliedValue = selectedItem[0].residueValue;
          this.validateForm(this.advancePaymentAppliedContract);
          this.formAdvancePayment.controls.cashReceipt.patchValue(selectedItem[0].cashReceipt);
          this.formAdvancePayment.controls.residueValue.patchValue(selectedItem[0].residueValue);
          this.clearSelectionTrigger++;
          this.getFormView(true);
        } else {
          const cashReceiptIds = selectedItem.map((item: { id: number; }) => item.id);
          try {
            await firstValueFrom(this.advancePaymentAppliedService.createSomeAdvancePaymentApplied(cashReceiptIds, this.cashReceiptModel.cashReceiptId));
            this.toastHandlerService.showToast('success', 'Los anticipos fueron agregados exitosamente.');
            this.getAdvancePaymentsApplied();
            this.cancel();
            this.updateAdvances.emit(true);
          } catch (error) {
            console.error('Error al agregar los anticipos.', error);
          }
        }
      }
    });
  }

  async onAction(event: { action: string, item?: CashReceiptPaymentCaptureTableModel, selectedItems?: number[] }) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para modificar recibos de caja. Comuníquese con el administrador del sistema.');
      return;
    }
    if (event.action === 'edit') {
      this.advancePaymentAppliedContract = await firstValueFrom(this.advancePaymentAppliedService.getAdvancePaymentAppliedById(event.item!.id));
      this.validateForm(this.advancePaymentAppliedContract);
      this.getFormView(true);
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteAdvancePayments(event.selectedItems);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getAdvancePaymentsApplied();
  }

  async onPageChangeAdvancePayments(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageAdvancePayments = event.page;
    this.limitAdvancePayments = event.pageSize;
    this.columnSortAdvancePayments = event.sortField || null;
    this.ascendantAdvancePayments = event.sortOrder === 1;
    await this.getListAdvancePaymentsForSelection();
  }

  async openAdvancePaymentsSelectionModal() {
    this.updateInputs();
    await this.getListAdvancePaymentsForSelection();
    this.advancePaymentsSelectionModalComponent && this.advancePaymentsSelectionModalComponent.show();
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async send() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para modificar recibos de caja. Comuníquese con el administrador del sistema.');
      return;
    }
    Object.keys(this.formAdvancePayment.controls).forEach(key => {
      const control = this.formAdvancePayment.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formAdvancePayment.controls.appliedValue.value === 0) {
      this.toastHandlerService.showToast('warning', 'El valor aplicado no puede ser 0.');
      return;
    }
    if (this.formAdvancePayment.valid) {
      const modelCashReceiptPayment = this.formAdvancePayment.getRawValue() as AdvancePaymentAppliedModel;

      if (this.advancePaymentAppliedContract.advancePaymentAppliedId === 0) {
        try {
          await firstValueFrom(this.advancePaymentAppliedService.createAdvancePaymentApplied(modelCashReceiptPayment));
          this.toastHandlerService.showToast(
            'success',
            'El anticipo fue aplicado exitosamente.'
          );
          this.getAdvancePaymentsApplied();
          this.cancel();
          this.updateAdvances.emit(true);
        } catch (error) {
          console.error('Error al aplicar el anticipo.', error);
        }
      } else {
        this.advancePaymentAppliedContract.appliedValue = modelCashReceiptPayment.appliedValue;

        try {
          await firstValueFrom(this.advancePaymentAppliedService.updateAdvancePaymentApplied(this.advancePaymentAppliedContract));
          this.validateForm(this.advancePaymentAppliedContract);
          this.toastHandlerService.showToast(
            'success',
            'El anticipo aplicado fue modificado exitosamente.'
          );
          this.getAdvancePaymentsApplied();
          this.cancel();
          this.updateAdvances.emit(true);
        } catch (error) {
          console.error('Error al actualizar el anticipo aplicado.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      appliedValue: {
        required: 'El campo Vr.Aplicado es obligatorio.',
        min: 'El Vr.Aplicado debe ser mayor o igual a 0.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formAdvancePayment.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateInputs() {
    this.inputsForAdvancePaymentsSelection$.next({
      headers: this.headerAdvancePayments,
      data: this.dataAdvancePayments,
      showCheckbox: true,
      showRecover: false,
      isMultipleSelect: true,
      filters: [],
    });
  }

  validateForm(advancePaymentApplied: AdvancePaymentAppliedModel) {
    this.formAdvancePayment = this.formBuilder.group({
      cashReceiptAppliedId: [advancePaymentApplied.cashReceiptAppliedId, [Validators.required, Validators.min(1)]],
      cashReceiptAdvanceId: [advancePaymentApplied.cashReceiptAdvanceId, [Validators.required, Validators.min(1)]],
      cashReceipt: [advancePaymentApplied.cashReceiptAdvance.consecutive, [Validators.required, Validators.min(1)]],
      residueValue: [advancePaymentApplied.cashReceiptAdvance.totalValue - advancePaymentApplied.appliedValue, [Validators.required, Validators.min(1)]],
      appliedValue: [advancePaymentApplied.appliedValue, [Validators.required, Validators.min(0)]]
    });
    this.formControlStateService.setControlState(this.formAdvancePayment, ['cashReceipt', 'residueValue'], false);
  }

}
